/**
 * scene_depth — THE VFX SNAPSHOT: the fire domain reads scene depth through its OWN node (2026-10-08).
 *
 * The owner's report: "if there was a fire and an npc or object moved in front of my view of the fire, I would still
 * see the fire when I should not." three r185 copies the depth buffer for `viewportDepthTexture()` ONCE per render, at
 * the first object drawn whose material reads that NODE INSTANCE, and `viewportLinearDepth` is one shared instance for
 * the whole program. The engine's water is OPAQUE and reads it (its thickness), so with water in view the copy was
 * taken in the middle of the opaque pass: every solid object drawn after the water — a body, a wall — was missing from
 * it. The volume flame draws with its depth test OFF and ends its march at that depth, so it was painted over whatever
 * stood in front of it (measured in the bed: ~1,580 flame pixels over a wall with a water sheet by the eye, 0 without).
 *
 * A private instance is latched on its own: its copy is taken at the first VFX draw, and every reader here is
 * transparent, so that is after ALL opaques, whatever else reads depth earlier. One node for the whole family (the
 * flame's three rungs, the plume, the fireball, the smoke columns, the blast) = one copy a render while any of them is
 * on screen. A new fire / smoke / blast material reads THIS, never `viewportLinearDepth`; and never give this node to
 * a material that is not transparent — it would move the copy back into the opaque pass for everyone.
 * Gate: verify/fire-occlusion-bed-probe.mjs (both backends; `PROBE_EXPECT=bug` is its before-leg).
 */
import { linearDepth, viewportDepthTexture, viewportLinearDepth } from 'three/tsl';

/** `localStorage atlas:vfx-depth = 'shared'` (read once, at load) puts three's shared snapshot back — the old behaviour,
 *  bug included: the bed's before-leg, and the A/B for the cost of the extra depth copy on a real GPU. */
export const VFX_DEPTH_SHARED: boolean = (() => {
  try { return globalThis.localStorage?.getItem('atlas:vfx-depth') === 'shared'; } catch { return false; }
})();

/** Linear scene depth (0 at the near plane, 1 at the far) behind the pixel, from the VFX's own post-opaque snapshot. */
export const vfxSceneLinearDepth = VFX_DEPTH_SHARED ? viewportLinearDepth : linearDepth(viewportDepthTexture());
